import LivingMap, {
  FilterKing,
  GlobalFilters,
  LivingMapPlugin,
  Utils,
} from "@livingmap/core-mapping";
import { Feature } from "geojson";
import mapboxgl from "mapbox-gl";
import { lineString, along, length } from "@turf/turf";

import {
  RouteQueryResponse,
  RouteMilestoneFeature,
} from "../../../redux/services/config";

import { EMPTY_DATA_SOURCE } from "./position-control";

import { EventTypes, LayerIds, SourceIds } from "./types";

const {
  createGeoJSONFeature,
  createGeoJSONFeatureCollection,
  createGeoJSONGeometryPoint,
} = Utils;

interface RoutePointProperties {
  route_icon: string;
  floor_id?: number;
}

interface Coordinate {
  x: number;
  y: number;
  floorId?: number;
}

const ANIMATION_MIN_DURATION = 3500;
const ANIMATION_MAX_DURATION = 6000;
const ANIMATION_MS_PER_COORDINATE = 10;
const ANIMATION_FPS = 60;

const easeInOutCubic = (t: number) => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};

class RoutingPlugin extends LivingMapPlugin {
  private filterInstance: FilterKing;
  private zoomLevel: number = 0;
  private routeLinePadding: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  } = { top: 0, bottom: 0, left: 0, right: 0 };
  private animationFrameInstance: number = 0;

  public constructor(id: string, LMMap: LivingMap, zoomLevel: number) {
    super(id, LMMap);
    this.filterInstance = LMMap.getFilterKing();
    this.zoomLevel = zoomLevel;
  }

  public activate(): void {
    this.setRouteVisualisation();
  }

  public deactivate(): void {
    this.clear();
  }

  private setRouteVisualisation(): void {
    // display the routing line with blue and grey
    this.filterInstance.updateLocalFilter(LayerIds.ROUTE_LINE_OTHER_LAYER, {
      globalExclusions: [GlobalFilters.FLOOR],
    });
    this.filterInstance.updateLocalFilter(
      `${LayerIds.ROUTE_LINE_OTHER_LAYER}-base`,
      { globalExclusions: [GlobalFilters.FLOOR] },
    );
  }

  public renderRoute(
    routeGeoJson: RouteQueryResponse["segments"][number]["routeGeoJson"],
    routeMilestones: RouteMilestoneFeature[],
    totalLength: number,
  ) {
    this.clear();
    const origin = routeGeoJson[0];
    const originCoordinates: Coordinate = {
      x: origin.geometry.coordinates[0][0],
      y: origin.geometry.coordinates[0][1],
      floorId: origin.properties.floorId,
    };

    const bounds = this.parseAndDisplayRoute(
      routeGeoJson,
      routeMilestones,
      originCoordinates,
    );

    this.handleMapDisplay(bounds);
    this.handleRouteAnimation(routeGeoJson, totalLength);
  }

  public clear(): void {
    cancelAnimationFrame(this.animationFrameInstance);

    const mapInstance = this.LMMap.getMapboxMap();
    const iconSources = mapInstance.getSource(
      SourceIds.ROUTE_ICON_SOURCE,
    ) as mapboxgl.GeoJSONSource;

    if (iconSources) iconSources.setData(EMPTY_DATA_SOURCE);

    const routeLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    if (routeLine) routeLine.setData(EMPTY_DATA_SOURCE);

    const routeOtherLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_OTHER_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    if (routeOtherLine) routeOtherLine.setData(EMPTY_DATA_SOURCE);

    const routeAnimatedLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_ANIMATED_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    if (routeAnimatedLine) routeAnimatedLine.setData(EMPTY_DATA_SOURCE);
  }

  private generatePoint(point: Coordinate, icon: string): Feature {
    const startPointGeometry = createGeoJSONGeometryPoint([point.x, point.y]);
    const featureProperties: RoutePointProperties = { route_icon: icon };

    if (point.floorId !== null) {
      featureProperties.floor_id = point.floorId;
    }

    const feature = createGeoJSONFeature(featureProperties, startPointGeometry);

    return feature;
  }

  private parseAndDisplayRoute(
    geoJsonData: RouteQueryResponse["segments"][number]["routeGeoJson"],
    routeMilestones: RouteMilestoneFeature[],
    from: Coordinate,
  ): mapboxgl.LngLatBoundsLike {
    const mapInstance = this.LMMap.getMapboxMap();

    const iconSources = mapInstance.getSource(
      SourceIds.ROUTE_ICON_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    const routeLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    const routeOtherLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_OTHER_SOURCE,
    ) as mapboxgl.GeoJSONSource;

    const features: Feature[] = [];
    routeMilestones.forEach((routeMilestone) => {
      const coordinate: Coordinate = {
        x: routeMilestone.geometry.coordinates[0],
        y: routeMilestone.geometry.coordinates[1],
      };

      if (routeMilestone.properties.originId) {
        coordinate.floorId = routeMilestone.properties.originId;
      }

      features.push(
        this.generatePoint(coordinate, routeMilestone.properties.mapIcon),
      );
    });

    const startFeatureCollection = createGeoJSONFeatureCollection(features);
    iconSources.setData(startFeatureCollection);

    const bounds = new mapboxgl.LngLatBounds();

    const startLgnLat = new mapboxgl.LngLat(from.x, from.y);
    bounds.extend(startLgnLat);

    const endLngLat = new mapboxgl.LngLat(from.x, from.y);
    bounds.extend(endLngLat);

    const geoJsonDataWithFloor = geoJsonData.map((feature) => {
      feature.geometry.coordinates.forEach((lngLatPair) => {
        bounds.extend(lngLatPair);
      });

      return {
        ...feature,
        properties: {
          ...feature.properties,
          floor_id: feature.properties.floorId,
        },
      };
    });

    routeLine.setData({
      type: "FeatureCollection",
      features: geoJsonDataWithFloor,
    });
    routeOtherLine.setData({
      type: "FeatureCollection",
      features: geoJsonDataWithFloor,
    });

    const exportBounds: mapboxgl.LngLatBoundsLike = [
      bounds.getSouthWest(),
      bounds.getNorthEast(),
    ];

    return exportBounds;
  }

  private handleRouteAnimation(
    geoJsonData: RouteQueryResponse["segments"][number]["routeGeoJson"],
    totalLength: number,
  ) {
    const mapInstance = this.LMMap.getMapboxMap();

    const animatedRouteLine = mapInstance.getSource(
      SourceIds.ROUTE_LINE_ANIMATED_SOURCE,
    ) as mapboxgl.GeoJSONSource;
    if (!animatedRouteLine) return;

    const featureCoordinates: [number, number][] = [];
    geoJsonData.forEach((feature) =>
      featureCoordinates.push(...feature.geometry.coordinates),
    );

    // Excluding the last point to avoid reversed steps at the end
    const route = lineString(featureCoordinates.slice(0, -1));
    // Calculate the total length of the route
    const routeLength = length(route, { units: "meters" });
    // Determine spacing between points along the route so that spacing depends on the route length, not a fixed step
    // Short routes: at least 0.1 meters spacing to avoid too many points
    const spacing = Math.max(totalLength, 0.1);

    const spacedCoordinates: number[][] = [];

    for (let dist = 0; dist < routeLength; dist += spacing) {
      spacedCoordinates.push(
        // Get coordinates at the current distance along the route
        along(route, dist, { units: "meters" }).geometry.coordinates,
      );
    }

    const coordinateCount = spacedCoordinates.length;

    // Determine total animation duration based on route length
    // - Short routes: at least ANIMATION_MIN_DURATION ms
    // - Long routes: capped at ANIMATION_MAX_DURATION ms
    // - Scaling factor: ANIMATION_MS_PER_COORDINATE
    const durationMs = Math.max(
      ANIMATION_MIN_DURATION,
      Math.min(
        ANIMATION_MAX_DURATION,
        coordinateCount * ANIMATION_MS_PER_COORDINATE,
      ),
    );

    // Calculate how many frames to run, based on FPS
    const totalFrames = Math.ceil(durationMs / (1000 / ANIMATION_FPS));

    let currentFrame = 0;

    // Animation function that runs once per frame
    const animateRoute = () => {
      if (currentFrame <= totalFrames) {
        // Calculate how far along the animation is (0 to 1)
        const percent = currentFrame / totalFrames;
        // Apply easing to make animation feel smooth
        const easedProgress = easeInOutCubic(percent);
        // Determine the current position along the route based on progress
        const currentIndex = Math.floor(easedProgress * coordinateCount);
        // Set the length of the visible "tail" (20% of the route)
        const windowSize = Math.floor(coordinateCount / 5);

        // Calculate start of the visible window, ensuring it doesn't go below 0
        const startIndex = Math.max(0, currentIndex - windowSize);

        // Get the subset of coordinates to render (the visible animated segment)
        const visibleCoords = spacedCoordinates.slice(startIndex, currentIndex);

        const feature = createGeoJSONFeature(
          {},
          {
            type: "LineString",
            coordinates: visibleCoords,
          },
        );

        animatedRouteLine.setData(createGeoJSONFeatureCollection([feature]));
        currentFrame++;
      } else {
        // Animation will be complete here so we reset state
        currentFrame = 0;
        animatedRouteLine.setData(createGeoJSONFeatureCollection([]));
      }

      this.animationFrameInstance = requestAnimationFrame(animateRoute);
    };

    // Cancel any previous animation if it exists
    if (this.animationFrameInstance) {
      cancelAnimationFrame(this.animationFrameInstance);
    }

    animateRoute();
  }

  public setRouteLinePadding(padding: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  }): void {
    this.routeLinePadding = padding;
  }

  public handleMapDisplay(bounds: mapboxgl.LngLatBoundsLike): void {
    const mapInstance = this.LMMap.getMapboxMap();
    const bearing = mapInstance.getBearing();

    try {
      mapInstance.fitBounds(bounds, {
        maxZoom: this.zoomLevel,
        padding: this.routeLinePadding,
        bearing,
      });

      this.LMMap.emit(EventTypes.ROUTE_DISPLAYED);
    } catch (e) {
      console.error(e);
    }
  }

  public handleFitMapToCoordinate(coords: mapboxgl.LngLatBoundsLike): void {
    const mapInstance = this.LMMap.getMapboxMap();
    const bearing = mapInstance.getBearing();

    const bounds = new mapboxgl.LngLatBounds();

    bounds.extend(coords);

    try {
      mapInstance.fitBounds(bounds, {
        maxZoom: this.zoomLevel,
        padding: this.routeLinePadding,
        bearing,
      });
    } catch (e) {
      console.error(e);
    }
  }
}

export default RoutingPlugin;
